import { getSearchOffersQueryOptions } from "@/components/marketplace/get-search-offers-query-options";
import { yearBoundsFromParam } from "@/lib/faceted-search-url";
import { FORMAT_CATEGORIES_WITH_OTHER } from "@garimpa/lib/formatGroups";
import { normalizeStringArray } from "@garimpa/lib/normalizeStringArray";

export type OffersSort = "best_price" | "price_drop" | "recent";
export type OffersMovement = "up" | "down" | "flat" | "unknown";

export const OFFERS_PAGE_SIZE = 40;

const OFFER_MOVEMENTS = [
  "up",
  "down",
  "flat",
  "unknown",
] as const satisfies readonly OffersMovement[];
const DIRECT_OFFERS_SORTS = ["best_price", "recent"] as const;
type DirectOffersSort = (typeof DIRECT_OFFERS_SORTS)[number];

function isOffersMovement(value: string | undefined): value is OffersMovement {
  return OFFER_MOVEMENTS.includes(value as OffersMovement);
}

function isDirectOffersSort(value: string | undefined): value is DirectOffersSort {
  return DIRECT_OFFERS_SORTS.includes(value as DirectOffersSort);
}

export interface OffersQueryInput {
  condition?: "new" | "used";
  artistId?: string | string[];
  currency: string;
  cursor?: number;
  format?: string;
  genre?: string | string[];
  movement?: string;
  popularity?: number;
  priceMin?: number;
  priceMax?: number;
  sort?: string;
  style?: string | string[];
  /** The work's original-year bounds in the shared facet `year` URL shape. */
  year?: number[];
}

export function normalizeOffersTaxonomyValues(
  value: string | string[] | undefined,
): string[] | undefined {
  const values = normalizeStringArray(value);
  return values.length ? values : undefined;
}

export function getOffersQueryOptions(input: OffersQueryInput) {
  const [yearFrom, yearTo] = yearBoundsFromParam(input.year);
  return getSearchOffersQueryOptions({
    includeMonitoredOfferCount: false,
    condition: input.condition ?? "new",
    artistIds: normalizeOffersTaxonomyValues(input.artistId),
    currency: input.currency,
    cursor: input.cursor,
    format: getFormat(input.format),
    genre: normalizeOffersTaxonomyValues(input.genre),
    movement: getMovement(input.movement),
    popularity: input.popularity,
    priceMin: input.priceMin,
    priceMax: input.priceMax,
    sort: getSort(input.sort),
    style: normalizeOffersTaxonomyValues(input.style),
    yearFrom,
    yearTo,
    limit: OFFERS_PAGE_SIZE,
  });
}

export function getFormat(value: string | undefined) {
  if (value === "all") return "all";
  if (
    FORMAT_CATEGORIES_WITH_OTHER.includes(value as (typeof FORMAT_CATEGORIES_WITH_OTHER)[number])
  ) {
    return value as (typeof FORMAT_CATEGORIES_WITH_OTHER)[number];
  }
  return "all";
}

export function getMovement(value: string | undefined): OffersMovement | undefined {
  if (isOffersMovement(value)) return value;
  return undefined;
}

export function getSort(value: string | undefined): OffersSort {
  if (isDirectOffersSort(value)) return value;
  return "price_drop";
}
